// MySQL procedural feed (ProceduralBodyIndexing Slice 3.1).
//
// Enumerates standalone stored procedures (`information_schema.ROUTINES`
// filtered by `ROUTINE_TYPE = 'PROCEDURE'`) + triggers
// (`information_schema.TRIGGERS`). Body comes back from
// `ROUTINE_DEFINITION` / `ACTION_STATEMENT` (no `CREATE PROCEDURE` /
// `CREATE TRIGGER` wrapper — only the inner compound block). Most MySQL
// routine bodies are wrapped in `BEGIN ... END`, which `coarseKind`
// classifies as `transaction-control` — we replace the outer keywords
// with spaces (preserving byte offsets) so the universal scanner walks
// `;`-separated inner statements and the SELECT bodies surface as
// `kind === 'select'`. Single-expression bodies (e.g. `SELECT … INTO …;`)
// pass through untouched. Mirror of `pgsqlProceduralFetcher`'s
// `stripPgProceduralWrapper` step 2 — same shape, no dollar-quote unwrap.

import type {
  ObjectKind,
  PersistedObjectListing,
  QualifiedName,
} from '../../types';

import {
  asString,
  escapeSqlLiteral,
  fetchListingRows,
  fetchRows,
  makeListing,
  type NavListingFetchOptions,
} from './queryHelper';

const SYSTEM_SCHEMAS = `('mysql', 'information_schema', 'performance_schema', 'sys')`;

const PROC_SQL = `SELECT ROUTINE_SCHEMA AS \`schema\`, ROUTINE_NAME AS name,
  ROUTINE_DEFINITION AS body
FROM information_schema.ROUTINES
WHERE ROUTINE_TYPE = 'PROCEDURE'
  AND ROUTINE_SCHEMA NOT IN ${SYSTEM_SCHEMAS}`;

const TRIGGER_SQL = `SELECT TRIGGER_SCHEMA AS \`schema\`, TRIGGER_NAME AS name,
  ACTION_STATEMENT AS body
FROM information_schema.TRIGGERS
WHERE TRIGGER_SCHEMA NOT IN ${SYSTEM_SCHEMAS}`;

function listing(
  objectKind: PersistedObjectListing['objectKind'],
  schema: string,
  name: string,
  bodyText: string,
): PersistedObjectListing {
  return makeListing('mysql', objectKind, schema, name, bodyText);
}

export function stripMysqlProceduralWrapper(body: string): string {
  const firstBegin = /\bBEGIN\b/i.exec(body);
  const lastEnd = /\bEND\b\s*;?\s*$/i.exec(body);
  if (!firstBegin || !lastEnd || firstBegin.index >= lastEnd.index) return body;
  const chars = body.split('');
  for (let i = firstBegin.index; i < firstBegin.index + firstBegin[0].length; i++) chars[i] = ' ';
  for (let i = lastEnd.index; i < lastEnd.index + lastEnd[0].length; i++) chars[i] = ' ';
  return chars.join('');
}

export async function listMysqlProceduralObjects(
  options: NavListingFetchOptions,
): Promise<readonly PersistedObjectListing[]> {
  const [procRows, triggerRows] = await Promise.all([
    fetchListingRows(options, {
      engine: 'mysql',
      label: 'mysql-procedures',
      sql: PROC_SQL,
      orderBy: 'ROUTINE_SCHEMA, ROUTINE_NAME',
      dialect: 'limit-offset',
      bodyColumns: ['body'],
    }),
    fetchListingRows(options, {
      engine: 'mysql',
      label: 'mysql-triggers',
      sql: TRIGGER_SQL,
      orderBy: 'TRIGGER_SCHEMA, TRIGGER_NAME',
      dialect: 'limit-offset',
      bodyColumns: ['body'],
    }),
  ]);

  const out: PersistedObjectListing[] = [];
  for (const row of procRows) {
    const name = asString(row.name);
    const body = asString(row.body);
    if (!name || !body) continue;
    out.push(listing('procedure', asString(row.schema), name, stripMysqlProceduralWrapper(body)));
  }
  for (const row of triggerRows) {
    const name = asString(row.name);
    const body = asString(row.body);
    if (!name || !body) continue;
    out.push(listing('trigger', asString(row.schema), name, stripMysqlProceduralWrapper(body)));
  }
  return out;
}

export async function fetchMysqlProceduralObjectBody(
  connectionId: string,
  database: string,
  objectKind: ObjectKind,
  objectName: QualifiedName,
): Promise<string | null> {
  const schema = objectName.schema ?? database;
  const s = escapeSqlLiteral(schema);
  const n = escapeSqlLiteral(objectName.name);

  if (objectKind === 'procedure') {
    const sql = `SELECT ROUTINE_DEFINITION AS body FROM information_schema.ROUTINES
      WHERE ROUTINE_SCHEMA = '${s}' AND ROUTINE_NAME = '${n}' AND ROUTINE_TYPE = 'PROCEDURE'`;
    const rows = await fetchRows(connectionId, database, sql, 'mysql-procedure-body');
    const raw = asString(rows[0]?.body);
    return raw ? stripMysqlProceduralWrapper(raw) : null;
  }

  if (objectKind === 'trigger') {
    const sql = `SELECT ACTION_STATEMENT AS body FROM information_schema.TRIGGERS
      WHERE TRIGGER_SCHEMA = '${s}' AND TRIGGER_NAME = '${n}'`;
    const rows = await fetchRows(connectionId, database, sql, 'mysql-trigger-body');
    const raw = asString(rows[0]?.body);
    return raw ? stripMysqlProceduralWrapper(raw) : null;
  }

  return null;
}
